import { useCallback, useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { toast } from 'sonner';

import { useSendMessage } from '@/hooks/useSendMessage';
import { selectLanguageModelOverride } from '@/modules/tryIt/redux/selectors';
import { trackEvent } from '@/segment/segment';
import { ModelStrategyStep } from '@/shared/types/modelStrategy';
import { EventName } from '@/shared/types/segment';
import { MessageSent } from '@/shared/types/tryIt';

import { useOutgoingModelStep } from './useOutgoingModel';
import { appendPendingTurn, sendFailed, sendStarted, sendSucceeded } from '../redux/actions';
import {
  selectBrainId,
  selectContext,
  selectTestPhase,
  selectTryItSessionId,
} from '../redux/selectors';
import { ConversationTurn } from '../types/conversation';
import { buildPendingTurn, buildTestTurn } from '../utils/utils';

/**
 * Project the user-picked model override (Model tab) onto the wire `metadata`
 * shape the orchestrator accepts. Returns undefined when empty so we don't
 * ship a no-op metadata field.
 */
const buildMessageOverrideMetadata = (
  override: ModelStrategyStep | null | undefined
): MessageSent['metadata'] => {
  if (!override) {
    return undefined;
  }
  const metadata: NonNullable<MessageSent['metadata']> = {};
  if (override.language_model_id) {
    metadata.language_model_id = override.language_model_id;
  }
  if (override.language_model_alias) {
    metadata.language_model_alias = override.language_model_alias;
  }
  if (override.reasoning_effort) {
    metadata.reasoning_effort = override.reasoning_effort;
  }
  return Object.keys(metadata).length > 0 ? metadata : undefined;
};

export type UseTestSendResult = { send: (text: string) => Promise<void>; isSending: boolean };

/**
 * Bridges `useSendMessage` (the raw TanStack mutation for the brain `/message`
 * endpoint) into the modal state machine. Owns:
 *
 * - building `ConversationTurn` objects via `buildTestTurn`
 * - dispatching state-machine transitions (`sendStarted` → `sendSucceeded` /
 *   `sendFailed`)
 * - threading the response `x-request-id` and per-turn context snapshots
 *   through Redux
 *
 * The hook does *not* hold its own pending flag — `isSending` comes from the
 * underlying mutation, and `isInputDisabled` is derived from the state-machine
 * phase via the selector tree.
 */
export const useTestSend = (): UseTestSendResult => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const brainId = useSelector(selectBrainId);
  const sessionId = useSelector(selectTryItSessionId);
  const storeContext = useSelector(selectContext);
  const languageModelOverride = useSelector(selectLanguageModelOverride);
  const outgoingModel = useOutgoingModelStep();
  const phase = useSelector(selectTestPhase);
  const { sendMessage, isPending } = useSendMessage();
  const location = useLocation();

  // Scope test sends to the active knowledge-base when Try-It is opened from a
  // KB route, so draft/document matching stays bounded.
  const collectionId = location.pathname.split('/knowledge-bases/')[1]?.split('/')[0];

  // Mirror the current session id in a ref so we can detect a reset that
  // happened while a send was in flight and drop the stale response.
  const sessionIdRef = useRef(sessionId);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  const send = useCallback(
    async (text: string) => {
      if (!brainId || !sessionId) {
        return;
      }

      trackEvent(EventName.SendTryItMessage, { brain_id: brainId, session_id: sessionId });

      const sessionIdAtStart = sessionId;
      dispatch(sendStarted());
      // Optimistically show the user's message immediately; replaced by the real
      // turn(s) on `sendSucceeded`, or dropped on `sendFailed`.
      dispatch(appendPendingTurn(buildPendingTurn({ text })));

      const message: MessageSent = {
        session_id: sessionId,
        input: { text },
        channel: 'test',
        context: storeContext.length > 0 ? storeContext[storeContext.length - 1] : {},
        collection_id: collectionId,
        match_draft: true,
        metadata: buildMessageOverrideMetadata(languageModelOverride),
      };

      try {
        const { data: responses, headers } = await sendMessage({ brainId, message });
        if (sessionIdRef.current !== sessionIdAtStart) {
          return;
        }
        const requestId = headers['x-request-id'] ?? null;

        const newTurns: ConversationTurn[] = [];
        const nextContext = [...storeContext];

        responses.forEach((response, index) => {
          const turnRequestId = requestId ?? `${response.session_id}-${index}`;
          newTurns.push(
            buildTestTurn({ response, requestId: turnRequestId, languageModel: outgoingModel })
          );
          nextContext.push(response.context);
        });

        if (newTurns.length === 0) {
          toast.error(t('try_it.empty_response_toast'));
        }
        dispatch(sendSucceeded({ turns: newTurns, requestId, context: nextContext }));
      } catch {
        if (sessionIdRef.current !== sessionIdAtStart) {
          return;
        }
        dispatch(sendFailed());
      }
    },
    [
      brainId,
      sessionId,
      storeContext,
      languageModelOverride,
      outgoingModel,
      collectionId,
      sendMessage,
      dispatch,
      t,
    ]
  );

  return { send, isSending: isPending || phase === 'sending' };
};
